import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { apiEndpoints } from "../../services/apiConfig";


export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await axios.post(apiEndpoints.login, credentials);
      return response.data;
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || "Login failed";
      return rejectWithValue({ message, data: err?.response?.data, status: err?.response?.status });
    }
  }
);

// --- SIGNUP ---
export const signupUser = createAsyncThunk(
  "auth/signupUser",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await axios.post(apiEndpoints.signUp, userData);
      return response.data;
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || "Signup failed";
      return rejectWithValue({ message, data: err?.response?.data, status: err?.response?.status });
    }
  }
);

// --- VERIFY OTP ---
export const verifyOtp = createAsyncThunk(
  "auth/verifyOtp",
  async ({ email, otp }, { rejectWithValue }) => {
    try {
      const response = await axios.post(apiEndpoints.verifyOtp, { email, otp });
      return response.data;
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || "OTP verification failed";
      return rejectWithValue({ message, data: err?.response?.data, status: err?.response?.status });
    }
  }
);

export const logoutUser = createAsyncThunk("auth/logoutUser", async () => {
  try {
    // Clear all storage
    localStorage.clear();
    return true; // or any success signal
  } catch (error) {
    console.error("Logout error:", error);
    throw error;
  }
});

// --- RESEND LOGIN OTP ---
export const resendLoginOtp = createAsyncThunk(
  "auth/resendLoginOtp",
  async ({ email }, { rejectWithValue }) => {
    try {
      const response = await axios.post(apiEndpoints.resendOtp, { email });
      return response.data;
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || "Resend OTP failed";
      return rejectWithValue({ message, data: err?.response?.data, status: err?.response?.status });
    }
  }
);


const initialState = {
  user: (() => {
    try {
      return JSON.parse(localStorage.getItem("user")) || null;
    } catch {
      return null;
    }
  })(),
  token: localStorage.getItem("token") || null,
  loading: false,
  error: null,
};



const authSlice = createSlice({
  name: "auth",
  initialState: {
    isAuthenticated: !!localStorage.getItem("token"),
  },
  reducers: {
  },
  extraReducers: (builder) => {
    // LOGIN
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = !!action.payload.access_token;
        localStorage.setItem("user", JSON.stringify(action.payload.user));
        localStorage.setItem("token", action.payload.access_token);
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(logoutUser.pending, (state) => {
        state.loading = null;
        state.error = null;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      });

    // SIGNUP
    builder
      .addCase(signupUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signupUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        localStorage.setItem("user", JSON.stringify(action.payload.user));
        localStorage.setItem("token", action.payload.token);
      })
      .addCase(signupUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // VERIFY OTP
    builder
      .addCase(verifyOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.loading = false;
        // Optionally update user/token if backend returns them after verification
        if (action.payload?.user) {
          state.user = action.payload.user;
          localStorage.setItem("user", JSON.stringify(action.payload.user));
        }
        if (action.payload?.token) {
          state.token = action.payload.token;
          localStorage.setItem("token", action.payload.token);
        }
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // RESEND LOGIN OTP
    builder
      .addCase(resendLoginOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resendLoginOtp.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(resendLoginOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

// =======================
//  Exports
// =======================
export const { logout } = authSlice.actions;
export default authSlice.reducer;
